import React, { useState, useEffect } from 'react';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { Notification } from '@progress/kendo-react-notification';
import { getAuthToken, handleAuthTokenError, BASEURI, advancedFilter } from '../utils';
import { useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import NoUsers from './components/NoUsers';

const DashboardSharingTable = ({ tabs, allUsers, onUpdateTabSharedUsers }) => {
    const [dashboards, setDashboards] = useState([]);
    const [editingRowId, setEditingRowId] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const location = useLocation();
    const [filteredSimpleUsers, setFilteredSimpleUsers] = useState([]);
    const [blinkActive, setBlinkActive] = useState(false);
    const [toasts, setToasts] = useState([]);
    const intl = useIntl();

    // Helper to show toast
    const showToast = (message, type = 'info') => {
        setToasts((prev) => [
            ...prev,
            { message, type, id: Date.now() + Math.random() }
        ]);
    };

    // Remove toast after 3s
    useEffect(() => {
        if (toasts.length > 0) {
            const timer = setTimeout(() => {
                setToasts((prev) => prev.slice(1));
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [toasts]);

    useEffect(()=>{
        setFilteredSimpleUsers(allUsers);
    }, [allUsers]);

    useEffect(() => {
        // If navigated with tabId in state, set editingRowId
        if (location.state && location.state.tabId) {
            setEditingRowId(location.state.tabId);
        }
    }, [location.state]);

    useEffect(() => {
        if (location.state?.focus) {
            setBlinkActive(true);
            const timer = setTimeout(() => setBlinkActive(false), 3000);
            return () => clearTimeout(timer);
        }
    }, [location.state?.focus]);

    useEffect(() => {
        const fetchInvitationsAndProcessDashboards = () => {
            let invitations = [];
            getAuthToken()
                .then(token => token)
                .catch(handleAuthTokenError)
                .then(token => {
                    if (token === false) {
                        console.error("Authentication token not found.");
                        return;
                    }
                    return fetch(`${BASEURI}/api/dashboard/invitations/sent`, {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                })
                .then(response => {
                    if (!response) return;
                    if (!response.ok) {
                        throw new Error('Failed to fetch invitations');
                    }
                    return response.json();
                })
                .then(data => {
                    console.log ('the data received for invitations', data);
                    if (!data) return;
                    invitations = data;
                    const processedDashboards = tabs.map(tab => {
                        const acceptedUsers = tab.shared_users
                            .map(userId => {
                                const user = allUsers.find(u => u.user === userId);
                                return user ? { ...user, status: 'accepted' } : null;
                            })
                            .filter(Boolean);

                        const invitedUsers = invitations
                            .filter(inv => inv.tab_details.id === tab.id && inv.status === 'pending')
                            .map(inv => {
                                const user = allUsers.find(u => u.user === inv.receiver_details.id);
                                return user ? { ...user, status: 'invited', invitationId: inv.id } : null;
                            })
                            .filter(Boolean);

                        const combinedUsers = [...acceptedUsers];
                        invitedUsers.forEach(invitedUser => {
                            if (!combinedUsers.some(u => u.user === invitedUser.user)) {
                                combinedUsers.push(invitedUser);
                            }
                        });

                        return {
                            ...tab,
                            shared_users: combinedUsers,
                        };
                    });
                    setDashboards(processedDashboards);
                })
                .catch(error => {
                    console.error("Failed to fetch invitations:", error);
                });
        };

        if (tabs.length > 0 && allUsers.length > 0) {
            fetchInvitationsAndProcessDashboards();
        }
    }, [tabs, allUsers]);

    const handleEdit = (id) => {
        setEditingRowId(id);
        setSelectedUser(null);
        // Reset filteredSimpleUsers to available users for this dashboard
        const dashboard = dashboards.find(d => d.id === id);
        if (dashboard) {
            setFilteredSimpleUsers(getAvailableUsersForInvite(dashboard));
        }
    };

    const handleCancel = () => {
        setEditingRowId(null);
        setSelectedUser(null);
    };

    const handleInviteUser = (dashboardId) => {
        if (!selectedUser) return;
        showToast(intl.formatMessage({ id: 'dashboardSharing.toast.sendingInvitation', defaultMessage: 'Sending invitation...' }), 'info');

        const dashboardToShare = tabs.find(d => d.id === dashboardId);
        if (!dashboardToShare) {
            showToast(intl.formatMessage({ id: 'dashboardSharing.toast.dashboardNotFound', defaultMessage: 'Could not find dashboard to share.' }), 'error');
            return;
        }

        const options = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json; charset=utf-8',
            },
            body: JSON.stringify({
                receiver: selectedUser.user,
                tab: dashboardId,
                message: `Please join my tab: ${dashboardToShare.name}`
            }),
        };

        getAuthToken()
            .then(token => token)
            .catch(handleAuthTokenError)
            .then(token => {
                if (token === false) {
                    showToast(intl.formatMessage({ id: 'dashboardSharing.toast.authError', defaultMessage: 'Authentication token not found.' }), 'error');
                    return;
                }
                options.headers.Authorization = `Bearer ${token}`;

                fetch(`${BASEURI}/api/dashboard/invitations/`, options)
                    .then(response => {
                        if (!response.ok) {
                            showToast(intl.formatMessage({ id: 'dashboardSharing.toast.failedInvitation', defaultMessage: 'Failed to send invitation.' }), 'error');
                            throw new Error('Failed to send invitation');
                        }
                        return response.json();
                    })
                    .then(newInvitation => {
                        showToast(intl.formatMessage({ id: 'dashboardSharing.toast.invitationSent', defaultMessage: 'Invitation sent!' }), 'success');
                        const updatedDashboards = dashboards.map((dashboard) => {
                            if (dashboard.id === dashboardId) {
                                const newUser = { ...selectedUser, status: 'invited', invitationId: newInvitation.id };
                                return {
                                    ...dashboard,
                                    shared_users: [...dashboard.shared_users, newUser],
                                };
                            }
                            return dashboard;
                        });
                        setDashboards(updatedDashboards);
                        setSelectedUser(null);
                    })
                    .catch(error => {
                        showToast(intl.formatMessage({ id: 'dashboardSharing.toast.errorSendingInvitation', defaultMessage: 'Error sending invitation.' }), 'error');
                        console.error("Error sending invitation:", error);
                    });
            });
    };

    const handleRemoveUser = (dashboardId, userToRemove) => {
        if (userToRemove.status === 'invited') {
            showToast(intl.formatMessage({ id: 'dashboardSharing.toast.removingInvitation', defaultMessage: 'Removing invitation...' }), 'info');
            const options = {
                method: 'DELETE',
                headers: { Authorization: '' },
            };

            getAuthToken()
                .then(token => token)
                .catch(handleAuthTokenError)
                .then(token => {
                    if (token === false) {
                        showToast(intl.formatMessage({ id: 'dashboardSharing.toast.authError', defaultMessage: 'Authentication token not found.' }), 'error');
                        return;
                    }
                    options.headers.Authorization = `Bearer ${token}`;

                    fetch(`${BASEURI}/api/dashboard/invitations/${userToRemove.invitationId}/`, options)
                        .then(response => {
                            if (!response.ok) {
                                showToast(intl.formatMessage({ id: 'dashboardSharing.toast.failedCancelInvitation', defaultMessage: 'Failed to cancel invitation.' }), 'error');
                                throw new Error('Failed to cancel invitation');
                            }
                            showToast(intl.formatMessage({ id: 'dashboardSharing.toast.invitationRemoved', defaultMessage: 'Invitation removed.' }), 'success');
                            const updatedDashboards = dashboards.map((dashboard) => {
                                if (dashboard.id === dashboardId) {
                                    return {
                                        ...dashboard,
                                        shared_users: dashboard.shared_users.filter((user) => user.user !== userToRemove.user),
                                    };
                                }
                                return dashboard;
                            });
                            setDashboards(updatedDashboards);
                        })
                        .catch(error => {
                            showToast(intl.formatMessage({ id: 'dashboardSharing.toast.errorCancelInvitation', defaultMessage: 'Error cancelling invitation.' }), 'error');
                            console.error("Error cancelling invitation:", error);
                        });
                });

        } 
        // Handle removing accepted users from the dashboard on the backend
        else if (userToRemove.status === 'accepted') {
            showToast(intl.formatMessage({ id: 'dashboardSharing.toast.removingUser', defaultMessage: 'Removing user...' }), 'info');
            getAuthToken()
                .then(token => token)
                .catch(handleAuthTokenError)
                .then(token => {
                    if (token === false) {
                        showToast(intl.formatMessage({ id: 'dashboardSharing.toast.authError', defaultMessage: 'Authentication token not found.' }), 'error');
                        return;
                    }
                    fetch(`${BASEURI}/api/dashboard-charts/tabs/${dashboardId}/remove-shared-user/${userToRemove.user}/`, {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    })
                    .then(response => {
                        if (!response.ok) {
                            showToast(intl.formatMessage({ id: 'dashboardSharing.toast.failedRemoveUser', defaultMessage: 'Failed to remove user.' }), 'error');
                            return response.json().then(data => { throw new Error(data.error || 'Failed to remove user'); });
                        }
                        showToast(intl.formatMessage({ id: 'dashboardSharing.toast.userRemoved', defaultMessage: 'User removed.' }), 'success');
                        // Optimistically update UI
                        const updatedDashboards = dashboards.map((dashboard) => {
                            if (dashboard.id === dashboardId) {
                                return {
                                    ...dashboard,
                                    shared_users: dashboard.shared_users.filter((user) => user.user !== userToRemove.user),
                                };
                            }
                            return dashboard;
                        });
                        setDashboards(updatedDashboards);
                        // Optionally, notify parent
                        if (onUpdateTabSharedUsers) {
                            const dashboard = dashboards.find(d => d.id === dashboardId);
                            const updatedSharedUserIds = dashboard.shared_users
                                .filter(u => u.user !== userToRemove.user)
                                .filter(u => u.status === 'accepted')
                                .map(u => u.user);
                            onUpdateTabSharedUsers(dashboardId, updatedSharedUserIds);
                        }
                    })
                    .catch(error => {
                        showToast(intl.formatMessage({ id: 'dashboardSharing.toast.errorRemoveUser', defaultMessage: 'Error removing user.' }), 'error');
                        console.error("Error removing accepted user:", error);
                    });
                });
        }
    };

    const getAvailableUsersForInvite = (dashboard) => {
        const sharedUserIds = dashboard.shared_users.map(u => u.user);
        return allUsers.filter(u => !sharedUserIds.includes(u.user));
    };

    // Add blinking style if focus is true
    const blinkStyle = `
        @keyframes blink-bg {
            0%, 100% { background-color: #e5e7eb; } /* gray-200 */
            50% { background-color: #d1d5db; } /* gray-300 */
        }
        .blink-row {
            animation: blink-bg 1s linear infinite;
        }
    `;

    return (
        <div className="p-4">
            <h1 className="text-md font-normal uppercase text-gray-600 ml-2 mb-2 tracking-wide">
                {intl.formatMessage({ id: 'dashboardSharing.title', defaultMessage: 'DASHBOARD SHARING' })}
            </h1>
            {/* Toasts: bottom right */}
            <style>
                {`
                    .k-notification .k-icon,
                    .k-notification .k-i-close {
                        color: #fff !important;
                    }
                `}
            </style>
            <div style={{
                position: 'fixed',
                bottom: 32,
                right: 32,
                zIndex: 9999,
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                minWidth: '300px',
                maxWidth: '90vw'
            }}>
                {toasts.map(toast => (
                    <Notification
                        key={toast.id}
                        type={{ style: toast.type, icon: true }}
                        closable={true}
                        onClose={() => setToasts((prev) => prev.filter(t => t.id !== toast.id))}
                    >
                        <span style={{
                            color: '#fff',
                            fontWeight: 500
                        }}>
                            {toast.message}
                        </span>
                    </Notification>
                ))}
            </div>
            {blinkActive && <style>{blinkStyle}</style>}
            <div className="overflow-x-auto bg-white rounded-lg shadow mx-4 mt-8">
                <table className="min-w-full table-fixed">
                    <thead className="bg-gray-100 border-b">
                        <tr>
                            <th className="text-left py-3 px-4 font-semibold text-sm w-1/3">
                                {intl.formatMessage({ id: 'dashboardSharing.table.dashboard', defaultMessage: 'Dashboard' })}
                            </th>
                            <th className="text-left py-3 px-4 font-semibold text-sm w-5/12">
                                {intl.formatMessage({ id: 'dashboardSharing.table.sharedWith', defaultMessage: 'Shared With' })}
                            </th>
                            <th className="text-left py-3 px-4 font-semibold text-sm w-1/4"></th>
                        </tr>
                    </thead>
                    <tbody className="text-gray-700">
                        {dashboards.map((dashboard) => (
                            <tr
                                key={dashboard.id}
                                className={`border-b hover:bg-gray-50 ${
                                    editingRowId === dashboard.id && blinkActive ? 'blink-row' : ''
                                }`}
                            >
                                <td className="py-3 px-4">{dashboard.name}</td>
                                <td className="py-3 px-4">
                                    {dashboard.shared_users.length > 0 ? (
                                        <div className="flex flex-wrap gap-2">
                                            {dashboard.shared_users.map((user) => (
                                                <div key={user.user} className="flex items-center bg-gray-200 rounded-full px-3 py-1 text-sm">
                                                    <span>{user.name}</span>
                                                    {user.status === 'invited' && (
                                                        <span className="ml-2 text-xs bg-blue-500 text-white rounded-full px-2 py-0.5">
                                                            {intl.formatMessage({ id: 'dashboardSharing.invited', defaultMessage: 'Invited' })}
                                                        </span>
                                                    )}
                                                    {editingRowId === dashboard.id && (
                                                        <button onClick={() => handleRemoveUser(dashboard.id, user)} className="ml-2 text-red-500 hover:text-red-700">&times;</button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <span className="text-gray-400">
                                            {intl.formatMessage({ id: 'dashboardSharing.notShared', defaultMessage: 'Not shared' })}
                                        </span>
                                    )}
                                </td>
                                <td className="py-3 px-4">
                                    {editingRowId === dashboard.id ? (
                                        <div className="flex flex-col space-y-2">
                                            <div className="flex items-center space-x-2">
                                                <DropDownList
                                                    listNoDataRender={NoUsers}
                                                    data={filteredSimpleUsers}
                                                    onFilterChange={(e) => {
                                                        // Always filter from available users for this dashboard
                                                        const availableUsers = getAvailableUsersForInvite(dashboard);
                                                        advancedFilter(
                                                            e.filter.value,
                                                            availableUsers,
                                                            (items) => setFilteredSimpleUsers(items),
                                                            ['username', 'name']
                                                        );
                                                    }}
                                                    textField="name"
                                                    dataItemKey="user"
                                                    value={selectedUser}
                                                    onChange={(e) => setSelectedUser(e.target.value)}
                                                    filterable={true}
                                                    placeholder={intl.formatMessage({ id: 'dashboardSharing.selectUser', defaultMessage: 'Select a user...' })}
                                                    className="w-full"
                                                />
                                            </div>
                                            <button onClick={() => handleInviteUser(dashboard.id)} className="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600 whitespace-nowrap" disabled={!selectedUser}>
                                                {intl.formatMessage({ id: 'dashboardSharing.invite', defaultMessage: 'Invite' })}
                                            </button>
                                            <button onClick={handleCancel} className="bg-gray-500 text-white px-3 py-1 rounded text-sm hover:bg-gray-600">
                                                {intl.formatMessage({ id: 'dashboardSharing.done', defaultMessage: 'Done' })}
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => handleEdit(dashboard.id)}
                                            className={`${dashboard.shared_users.length > 0
                                                ? 'bg-yellow-500 hover:bg-yellow-600'
                                                : 'bg-green-500 hover:bg-green-600'
                                                } text-white px-3 py-1 rounded text-sm`}
                                        >
                                            {dashboard.shared_users.length > 0
                                                ? intl.formatMessage({ id: 'dashboardSharing.edit', defaultMessage: 'Edit' })
                                                : intl.formatMessage({ id: 'dashboardSharing.add', defaultMessage: 'Add' })}
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default DashboardSharingTable;
